import { useRef } from "react";
import { useSubmitAnalyticMutation } from "redux/queries/analytics";

const getInitialData = () => ({
  featureEntry: null,
  cameraOpened: null,
  frameSent: null,
  eligibleCount: 0,
  nonEligibleCount: 0,
  errorCount: 0,
  os: null,
  totalTimeSpent: null,
  nationality: null,
  gender: null,
  dateOfBirth: null,
  t_image_submitted: null,
  t_ocr_start: null,
  t_ocr_end: null,
  t_parse_start: null,
  t_parse_end: null,
  t_validation_start: null,
  t_validation_end: null,
  t_flow_end: null,
  sent: false,
});

export default function useScanAnalytics(areAnalyticsEnabled = true) {
  const [submitAnalytic] = useSubmitAnalyticMutation();
  const analyticsRef = useRef(getInitialData());

  // If analytics are disabled, return a no-op API
  if (!areAnalyticsEnabled) {
    const noop = () => {};
    return {
      markFeatureEntry: noop,
      markCameraOpened: noop,
      markFrameSent: noop,
      sendAnalyticsIfNeeded: noop,
      incrementEligible: noop,
      incrementNonEligible: noop,
      incrementError: noop,
      setOS: noop,
      setNationality: noop,
      setGender: noop,
      setDateOfBirth: noop,
      markTotalTimeSpent: noop,
      resetAnalytics: noop,
      markImageSubmitted: noop,
      setBackendTimestamps: noop,
      markFlowEnd: noop,
      analytics: {},
    };
  }

  function update(patch) {
    analyticsRef.current = { ...analyticsRef.current, ...patch };
  }

  function markFeatureEntry() {
    if (!analyticsRef.current.featureEntry) {
      update({ featureEntry: Date.now() });
    }
  }

  function markCameraOpened() {
    if (!analyticsRef.current.cameraOpened) {
      update({ cameraOpened: Date.now() });
    }
  }

  function markFrameSent() {
    update({ frameSent: Date.now() });
  }

  function setOS(osString) {
    update({ os: osString });
  }

  function incrementEligible() {
    update({ eligibleCount: (analyticsRef.current.eligibleCount || 0) + 1 });
  }

  function incrementNonEligible() {
    update({
      nonEligibleCount: (analyticsRef.current.nonEligibleCount || 0) + 1,
    });
  }

  function incrementError() {
    update({ errorCount: (analyticsRef.current.errorCount || 0) + 1 });
  }

  function markTotalTimeSpent() {
    if (
      !analyticsRef.current.totalTimeSpent &&
      analyticsRef.current.frameSent
    ) {
      const now = Date.now();
      update({ totalTimeSpent: now - analyticsRef.current.frameSent });
    }
  }

  function sendAnalyticsIfNeeded(extra = {}) {
    if (analyticsRef.current.sent) return;
    const {
      featureEntry,
      cameraOpened,
      frameSent,
      eligibleCount,
      nonEligibleCount,
      errorCount,
      os,
      totalTimeSpent,
      nationality,
      gender,
      dateOfBirth,
      t_image_submitted,
      t_ocr_start,
      t_ocr_end,
      t_parse_start,
      t_parse_end,
      t_validation_start,
      t_validation_end,
      t_flow_end,
    } = analyticsRef.current;

    // Accept detail and reason from extra
    const { reason, detail } = extra;
    const dataToSend = {
      type: "scan_timing",
      featureEntry,
      cameraOpened,
      frameSent,
      eligibleCount,
      nonEligibleCount,
      errorCount,
      os,
      totalTimeSpent,
      nationality,
      gender,
      dateOfBirth,
      t_image_submitted,
      t_ocr_start,
      t_ocr_end,
      t_parse_start,
      t_parse_end,
      t_validation_start,
      t_validation_end,
      t_flow_end,
      reason,
      detail,
      ...extra,
    };
    submitAnalytic(dataToSend);
    update({ sent: true });
  }

  function setNationality(nationality) {
    update({ nationality });
  }

  function setGender(gender) {
    update({ gender });
  }

  function setDateOfBirth(dateOfBirth) {
    update({ dateOfBirth });
  }

  function markImageSubmitted() {
    if (!analyticsRef.current.t_image_submitted) {
      update({ t_image_submitted: Date.now() });
    }
  }

  function setBackendTimestamps(timestamps) {
    // Accept backend timestamps: t_ocr_start, t_ocr_end, t_parse_start, t_parse_end, t_validation_start, t_validation_end
    update({
      t_ocr_start: timestamps.t_ocr_start || null,
      t_ocr_end: timestamps.t_ocr_end || null,
      t_parse_start: timestamps.t_parse_start || null,
      t_parse_end: timestamps.t_parse_end || null,
      t_validation_start: timestamps.t_validation_start || null,
      t_validation_end: timestamps.t_validation_end || null,
    });
  }

  function markFlowEnd() {
    if (!analyticsRef.current.t_flow_end) {
      update({ t_flow_end: Date.now() });
    }
  }

  function resetAnalytics() {
    analyticsRef.current = getInitialData();
  }

  return {
    markFeatureEntry,
    markCameraOpened,
    markFrameSent,
    sendAnalyticsIfNeeded,
    incrementEligible,
    incrementNonEligible,
    incrementError,
    setOS,
    setNationality,
    setGender,
    setDateOfBirth,
    markTotalTimeSpent,
    resetAnalytics,
    markImageSubmitted,
    setBackendTimestamps,
    markFlowEnd,
    analytics: analyticsRef.current,
  };
}
